import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { SlackInstallation } from "@veritab/api-contracts";
import { AlertTriangle, CheckCircle2, ExternalLink, FileSpreadsheet, MessageCircle, X } from "lucide-react";
import { useEffect, useState } from "react";

import { BankStatementCard } from "@/features/bank-statements";
import { ExcelUploadCard } from "@/features/excel-uploads";
import {
  addAllowedDriveFile,
  disconnectSlackInstallation,
  getGoogleConnectUrl,
  getGoogleStatus,
  getSlackConnectUrl,
  listAllowedDriveFiles,
  listSlackInstallations,
  removeAllowedDriveFile,
  searchDriveFiles,
} from "@/features/integrations/api/integrations";
import { TallyConnectionCard } from "@/features/integrations/TallyConnectionCard";
import { TcsionConnectionCard } from "@/features/integrations/TcsionConnectionCard";
import { WhatsAppCard } from "@/features/integrations/WhatsAppCard";
import { usePageHeader } from "@/shared/store/page-header";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui";

type IntegrationsSearch = { error?: string };

export const Route = createFileRoute("/_authenticated/integrations/")({
  component: IntegrationsPage,
  validateSearch: (search: Record<string, unknown>): IntegrationsSearch => {
    return {
      error: typeof search.error === "string" ? search.error : undefined,
    };
  },
});

function IntegrationsPage() {
  const [showError, setShowError] = useState(true);
  const navigate = useNavigate();
  const { setTitle, reset } = usePageHeader();

  useEffect(() => {
    setTitle("Integrations");
    return () => reset();
  }, [setTitle, reset]);

  // Route.useSearch() is properly typed by validateSearch, but TypeScript inference
  // may not work until route tree is regenerated
  const searchParams = Route.useSearch();
  const { error } = searchParams;

  const handleCloseError = () => {
    setShowError(false);
    // Clear the error from the URL by navigating to the same route without the error param
    void navigate({ to: "." });
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Integrations</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage your data source integrations</p>
        </div>
      </div>

      {error && showError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-start justify-between gap-4">
          <p className="text-sm text-destructive">{String(error)}</p>
          <button
            onClick={handleCloseError}
            className="text-destructive hover:text-destructive/80 transition-colors flex-shrink-0"
            aria-label="Close error message"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <GoogleSheetsCard />
        <SlackCard />
        <TallyConnectionCard />
        <TcsionConnectionCard />
        <ExcelUploadCard />
        <BankStatementCard />
        <WhatsAppCard />
      </div>
    </div>
  );
}

function SlackCard() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [pendingDisconnect, setPendingDisconnect] = useState<SlackInstallation | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["slack-installations"],
    queryFn: listSlackInstallations,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const installations = data?.installations ?? [];
  const activeInstallations = installations.filter((installation) => installation.status === "active");
  const revokedInstallations = installations.filter((installation) => installation.status === "revoked");

  const disconnectMutation = useMutation({
    mutationFn: disconnectSlackInstallation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["slack-installations"] });
      setPendingDisconnect(null);
    },
  });

  const handleConnect = (reconnectTeamId?: string) => {
    setIsConnecting(true);
    window.location.href = getSlackConnectUrl({
      redirect: "/integrations",
      reconnectTeamId,
    });
  };

  return (
    <Card className="border-l-4 border-l-[#4A154B]">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#4A154B] text-white">
              <MessageCircle className="size-5" />
            </div>
            <div>
              <CardTitle>Slack</CardTitle>
              <CardDescription>Receive Veritab insights in Slack DMs</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Install the Slack app to use Veritab in direct messages with your workspace data context.
        </p>
        {!isLoading && (
          <div className="mt-4 space-y-3">
            {installations.length === 0 && (
              <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
                No Slack workspaces connected yet.
              </div>
            )}

            {installations.map((installation) => {
              const teamLabel = installation.teamName || installation.teamId;
              const isRevoked = installation.status === "revoked";
              return (
                <div
                  key={installation.teamId}
                  className="rounded-lg border bg-muted/30 p-3 flex items-start justify-between gap-3"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{teamLabel}</span>
                      <Badge variant={isRevoked ? "outline" : "default"}>
                        {isRevoked ? "Revoked" : "Active"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">Team ID: {installation.teamId}</p>
                    {isRevoked && (
                      <p className="text-xs text-amber-700">
                        {installation.revokedReason
                          ? `Reconnect required (${installation.revokedReason}).`
                          : "Reconnect required."}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {isRevoked ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleConnect(installation.teamId)}
                        disabled={isConnecting}
                      >
                        Reconnect
                        <ExternalLink className="ml-2 size-3" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPendingDisconnect(installation)}
                        disabled={disconnectMutation.isPending}
                      >
                        Disconnect
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && installations.length > 0 && (
          <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <CheckCircle2 className="size-3 text-emerald-600" />
              Active: {activeInstallations.length}
            </span>
            <span className="flex items-center gap-1">
              <AlertTriangle className="size-3 text-amber-600" />
              Revoked: {revokedInstallations.length}
            </span>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button
          variant="default"
          className="w-full"
          onClick={() => handleConnect()}
          disabled={isLoading || isConnecting}
        >
          {isLoading ? "Loading..." : isConnecting ? "Redirecting..." : "Connect another workspace"}
          <ExternalLink className="ml-2 size-4" />
        </Button>
      </CardFooter>

      <Dialog open={Boolean(pendingDisconnect)} onOpenChange={(open) => !open && setPendingDisconnect(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect Slack workspace</DialogTitle>
            <DialogDescription>
              {pendingDisconnect
                ? `Disconnect ${pendingDisconnect.teamName || pendingDisconnect.teamId}? This only affects this workspace.`
                : "Disconnect this Slack workspace?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDisconnect(null)}
              disabled={disconnectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!pendingDisconnect) return;
                disconnectMutation.mutate(pendingDisconnect.teamId);
              }}
              disabled={!pendingDisconnect || disconnectMutation.isPending}
            >
              {disconnectMutation.isPending ? "Disconnecting..." : "Disconnect workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function GoogleSheetsCard() {
  const [isConnecting, setIsConnecting] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ["google-status"],
    queryFn: getGoogleStatus,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const isConnected = status?.connected ?? false;

  const handleConnect = () => {
    setIsConnecting(true);
    window.location.href = getGoogleConnectUrl("/integrations");
  };

  return (
    <Card className="border-l-4 border-l-[#34A853]">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#34A853] text-white">
              <FileSpreadsheet className="size-5" />
            </div>
            <div>
              <CardTitle>Google Sheets</CardTitle>
              <CardDescription>Export financial data to your Drive</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Connect your Google account to let Veritab AI export formatted spreadsheets directly to your
          Google Drive when you ask.
        </p>
        {!isLoading && (
          <div className="mt-4 rounded-lg border bg-muted/30 p-3">
            {isConnected ? (
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="size-4 text-emerald-600" />
                <span className="font-medium">Connected</span>
                {status?.googleEmail && (
                  <span className="text-muted-foreground">({status.googleEmail})</span>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Not connected</div>
            )}
          </div>
        )}

        {isConnected && <DriveAllowlistSection />}
      </CardContent>
      <CardFooter>
        <Button
          variant={isConnected ? "outline" : "default"}
          className="w-full"
          onClick={handleConnect}
          disabled={isConnecting || isLoading}
        >
          {isLoading
            ? "Loading..."
            : isConnecting
              ? "Redirecting..."
              : isConnected
                ? "Reconnect"
                : "Connect Google Sheets"}
          <ExternalLink className="ml-2 size-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}

/**
 * Section embedded inside the Google Sheets card. Lets the org admin pick
 * exactly which Drive files the Veritab platform may read — every agent
 * (analysis, ontology builder) is restricted to this allowlist.
 */
function DriveAllowlistSection() {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Debounce the search input so we don't fire on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(searchInput.trim()), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const allowedQuery = useQuery({
    queryKey: ["google-allowed-files"],
    queryFn: listAllowedDriveFiles,
    staleTime: 30_000,
  });

  const searchQuery = useQuery({
    queryKey: ["google-drive-search", debouncedQuery],
    queryFn: () => searchDriveFiles(debouncedQuery),
    enabled: showSearch,
    staleTime: 10_000,
  });

  const addMutation = useMutation({
    mutationFn: (driveFileId: string) => addAllowedDriveFile(driveFileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["google-allowed-files"] });
      void queryClient.invalidateQueries({ queryKey: ["google-drive-search"] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (driveFileId: string) => removeAllowedDriveFile(driveFileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["google-allowed-files"] });
      void queryClient.invalidateQueries({ queryKey: ["google-drive-search"] });
    },
  });

  const allowed = allowedQuery.data ?? [];

  return (
    <div className="mt-4 rounded-lg border bg-background p-3 space-y-3">
      <div>
        <h3 className="text-sm font-medium">Files allowed for agents</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Agents can only see Drive files you allow here. This applies across chat
          and ontology ingestion.
        </p>
      </div>

      {allowedQuery.isLoading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : allowed.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">
          No files allowed yet. Search below to add one.
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {allowed.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-2 rounded border bg-muted/40 px-2 py-1.5 text-xs"
            >
              <DriveTypeBadge type={f.fileType} />
              <span className="flex-1 truncate" title={f.fileName}>
                {f.fileName}
              </span>
              <button
                onClick={() => {
                  if (
                    window.confirm(
                      `Revoke agent access to "${f.fileName}"? Agents will no longer be able to read it.`
                    )
                  ) {
                    removeMutation.mutate(f.driveFileId);
                  }
                }}
                disabled={removeMutation.isPending}
                className="text-destructive hover:underline disabled:opacity-50"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t pt-3">
        {!showSearch ? (
          <Button variant="outline" size="sm" onClick={() => setShowSearch(true)}>
            Add files…
          </Button>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search Drive by name (leave empty for recent files)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="flex-1 rounded border bg-background px-2 py-1 text-xs"
              />
              <Button variant="ghost" size="sm" onClick={() => setShowSearch(false)}>
                Done
              </Button>
            </div>
            {searchQuery.isLoading && (
              <div className="text-xs text-muted-foreground">Searching…</div>
            )}
            {searchQuery.isError && (
              <div className="text-xs text-destructive">
                Search failed: {(searchQuery.error).message}
              </div>
            )}
            {searchQuery.data && searchQuery.data.length === 0 && (
              <div className="text-xs text-muted-foreground italic">
                No files match — try a different query.
              </div>
            )}
            {searchQuery.data && searchQuery.data.length > 0 && (
              <ul className="space-y-1 max-h-60 overflow-y-auto pr-1">
                {searchQuery.data.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center gap-2 rounded border bg-muted/30 px-2 py-1.5 text-xs"
                  >
                    <DriveTypeBadge type={f.fileType} />
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 truncate hover:underline"
                      title={f.name}
                    >
                      {f.name}
                    </a>
                    {f.alreadyAllowed ? (
                      <span className="text-emerald-600 text-[11px]">✓ Allowed</span>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addMutation.mutate(f.id)}
                        disabled={addMutation.isPending}
                      >
                        {addMutation.isPending ? "…" : "Add"}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DriveTypeBadge({
  type,
}: {
  type: "google_sheet" | "excel_xlsx" | "excel_xls" | "other";
}) {
  const { label, color } =
    type === "google_sheet"
      ? { label: "Sheets", color: "text-[#34A853]" }
      : type === "excel_xlsx" || type === "excel_xls"
        ? { label: "Excel", color: "text-[#107C41]" }
        : { label: "File", color: "text-muted-foreground" };
  return (
    <span className={`text-[10px] font-medium uppercase tracking-wide ${color} w-12`}>
      {label}
    </span>
  );
}
